import React, { useEffect } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Timer,
  ShieldCheck,
  Users,
  HardHat,
  Search,
  Layers,
  MountainSnow,
  Droplets,
  Printer,
  ArrowLeft,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    icon: HardHat,
    title: "Gros travaux",
    desc: "Rénovation complète et réfection d'étanchéité sur toiture-terrasse, dalles et structures bâtiment.",
  },
  {
    icon: Search,
    title: "Recherche de fuites",
    desc: "Diagnostic précis par fumigène, colorant ou thermographie pour localiser l'origine exacte d'une infiltration.",
  },
  {
    icon: Layers,
    title: "Intervention ponctuelle",
    desc: "Audit technique, reprise localisée et traitement ciblé sans démontage lourd.",
  },
  {
    icon: MountainSnow,
    title: "Entretien toitures",
    desc: "Contrats d'entretien périodiques : nettoyage, vérification des relevés, étanchéité préventive.",
  },
  {
    icon: Droplets,
    title: "Résines & revêtements",
    desc: "Travaux de résine, étanchéité liquide, peinture réfléchissante anti-chaleur et revêtements de sol.",
  },
];

const PILLARS = [
  { icon: Award, title: "Expertise métier", desc: "Étancheurs qualifiés, maîtrisant bitume, PVC, EPDM et résines techniques." },
  { icon: Timer, title: "Réactivité 24h", desc: "Mobilisation rapide sur toute l'Île-de-France dès détection d'une infiltration." },
  { icon: ShieldCheck, title: "Garantie décennale", desc: "Chaque intervention est couverte et documentée pour votre tranquillité." },
  { icon: Users, title: "Interlocuteur unique", desc: "De l'audit à la réception du chantier, un chargé d'affaires dédié." },
];

const CLIENT_TYPES = [
  "Syndics de copropriété",
  "Bailleurs sociaux",
  "Entreprises tertiaires",
  "Promoteurs & maîtres d'ouvrage",
  "Particuliers propriétaires",
  "Architectes & bureaux d'études",
];

const PROCESS = [
  { n: "01", title: "Contact & visite", desc: "Vous nous appelez, nous nous déplaçons gratuitement pour un diagnostic sur site." },
  { n: "02", title: "Devis détaillé", desc: "Étude technique, choix des matériaux, devis transparent sous 48h." },
  { n: "03", title: "Réalisation", desc: "Chantier mené par nos équipes qualifiées, dans le respect des délais." },
  { n: "04", title: "Garantie & suivi", desc: "Réception de chantier, garantie décennale et entretien proposé." },
];

export default function Presentation() {
  useEffect(() => {
    document.title = "ETANCIA · Plaquette commerciale";
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="bg-slate-200 min-h-screen">
      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/"
            data-testid="presentation-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#003366] hover:text-[#FF5A1F]"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au site
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-500">
              Astuce : choisissez « Enregistrer au format PDF » dans la fenêtre d'impression.
            </span>
            <button
              onClick={handlePrint}
              data-testid="presentation-print"
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04812] text-white font-semibold px-5 py-2.5 text-sm transition-all hover:-translate-y-0.5 shadow-sm"
            >
              <Printer className="w-4 h-4" /> Télécharger en PDF
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0">
        <div className="plaquette mx-auto bg-white shadow-2xl print:shadow-none">
          {/* PAGE 1 — COVER */}
          <section className="page page-cover relative overflow-hidden bg-[#003366] text-white">
            <div className="absolute inset-0 diag-lines opacity-30 pointer-events-none" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF5A1F]/20 rounded-full blur-3xl" />

            <div className="relative h-full flex flex-col justify-between p-14">
              <div className="bg-white inline-flex p-3 self-start">
                <img
                  src="/assets/etancia-logo.png"
                  alt="ETANCIA"
                  className="h-14 w-auto object-contain"
                />
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-block w-12 h-px bg-[#FF5A1F]" />
                  <span className="text-[10px] uppercase tracking-[0.32em] font-semibold text-[#FF9A6F]">
                    Plaquette commerciale · 2025
                  </span>
                </div>
                <h1 className="font-display font-extrabold text-6xl leading-[0.95] tracking-tight">
                  L'étanchéité <br />
                  <span className="text-[#FF5A1F]">qui tient,</span>
                  <br /> sans compromis.
                </h1>
                <p className="mt-8 max-w-md text-slate-200/90 leading-relaxed text-base">
                  Spécialiste étanchéité toiture-terrasse en Île-de-France.
                  Gros travaux, recherche de fuites, entretien — par des
                  étancheurs qualifiés, garantie décennale.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-0 border border-white/20">
                {[
                  { k: "10 ans", v: "Garantie décennale" },
                  { k: "24h", v: "Intervention rapide" },
                  { k: "100%", v: "Île-de-France" },
                ].map((s, i) => (
                  <div
                    key={i}
                    className={`p-5 ${i !== 2 ? "border-r border-white/20" : ""}`}
                  >
                    <div className="font-display text-3xl font-extrabold text-[#FF5A1F]">
                      {s.k}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-200/80 mt-1">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* PAGE 2 — QUI SOMMES-NOUS */}
          <section className="page bg-white">
            <div className="p-14 h-full flex flex-col">
              <PageHeader index="01" label="Qui sommes-nous" />
              <h2 className="font-display font-extrabold text-5xl tracking-tight text-[#0F172A] leading-[1] mt-8">
                Le sérieux d'un bureau d'études.
                <br />
                <span className="text-[#FF5A1F]">La vitesse d'un artisan.</span>
              </h2>

              <p className="mt-8 text-slate-600 leading-relaxed text-[15px] max-w-2xl">
                Basée à <strong>Ris-Orangis (91130)</strong>, ETANCIA est une
                entreprise d'étanchéité tous corps d'état qui intervient sur
                toute l'Île-de-France. Nous accompagnons syndics, bailleurs,
                entreprises et particuliers sur l'ensemble du cycle de vie de
                leurs toitures-terrasses : audit, réfection, recherche de
                fuites, et entretien.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-0 border-l border-t border-slate-200">
                {PILLARS.map((p, i) => (
                  <div
                    key={i}
                    className="border-r border-b border-slate-200 p-7"
                  >
                    <p.icon className="w-6 h-6 text-[#FF5A1F]" />
                    <h3 className="mt-4 font-display font-bold text-lg text-[#0F172A]">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-auto pt-10 flex items-end justify-between">
                <div>
                  <div className="label-kicker text-slate-400">Notre vocation</div>
                  <p className="font-display font-bold text-xl text-[#003366] mt-2 max-w-md leading-tight">
                    Garantir la pérennité de votre bâti, mètre carré après mètre carré.
                  </p>
                </div>
                <PageFooter pageNum="02" />
              </div>
            </div>
          </section>

          {/* PAGE 3 — NOS PRESTATIONS */}
          <section className="page bg-[#F2F4F7]">
            <div className="p-14 h-full flex flex-col">
              <PageHeader index="02" label="Nos prestations" />
              <h2 className="font-display font-extrabold text-5xl tracking-tight text-[#0F172A] leading-[1] mt-8">
                Un métier, <span className="text-[#FF5A1F]">cinq expertises.</span>
              </h2>

              <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-slate-200 bg-white">
                {SERVICES.map((s, i) => (
                  <div
                    key={i}
                    className="border-r border-b border-slate-200 p-6 flex flex-col"
                  >
                    <div className="w-11 h-11 bg-[#003366] text-white flex items-center justify-center">
                      <s.icon className="w-5 h-5" />
                    </div>
                    <h3 className="mt-5 font-display font-bold text-lg text-[#0F172A]">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-[#003366] text-white p-7 flex items-center justify-between gap-6">
                <div>
                  <div className="label-kicker text-[#FF9A6F]">
                    Matériaux & techniques
                  </div>
                  <p className="font-display font-bold text-lg mt-2">
                    Bitume · PVC · EPDM · Résine polyuréthane · Membranes auto-protégées
                  </p>
                </div>
                <span className="text-5xl font-display font-extrabold text-[#FF5A1F]">
                  +
                </span>
              </div>

              <div className="mt-auto pt-10 flex justify-end">
                <PageFooter pageNum="03" />
              </div>
            </div>
          </section>

          {/* PAGE 4 — METHODE & CLIENTELE */}
          <section className="page bg-white">
            <div className="p-14 h-full flex flex-col">
              <PageHeader index="03" label="Notre méthode" />
              <h2 className="font-display font-extrabold text-5xl tracking-tight text-[#0F172A] leading-[1] mt-8">
                4 étapes,
                <br /> <span className="text-[#FF5A1F]">zéro mauvaise surprise.</span>
              </h2>

              <div className="mt-10 grid grid-cols-4 gap-0 border-l border-t border-slate-200">
                {PROCESS.map((p, i) => (
                  <div
                    key={i}
                    className={`border-r border-b border-slate-200 p-6 ${
                      i % 2 === 0 ? "bg-[#F2F4F7]" : "bg-white"
                    }`}
                  >
                    <div className="font-display font-extrabold text-3xl text-[#FF5A1F]">
                      {p.n}
                    </div>
                    <h3 className="mt-4 font-display font-bold text-base text-[#0F172A]">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-12">
                <div className="label-kicker text-slate-400">Ils nous font confiance</div>
                <h3 className="mt-3 font-display font-extrabold text-2xl text-[#0F172A]">
                  Une clientèle exigeante & diversifiée
                </h3>
                <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3">
                  {CLIENT_TYPES.map((c) => (
                    <div key={c} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-[#003366] text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                      <span className="font-display font-semibold text-[15px] text-[#0F172A]">
                        {c}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-auto pt-10 flex justify-end">
                <PageFooter pageNum="04" />
              </div>
            </div>
          </section>

          {/* PAGE 5 — CONTACT (BACK COVER) */}
          <section className="page bg-[#003366] text-white relative overflow-hidden">
            <div className="absolute inset-0 diag-lines opacity-20 pointer-events-none" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#FF5A1F]/20 rounded-full blur-3xl" />

            <div className="relative p-14 h-full flex flex-col">
              <PageHeader index="04" label="Contactez-nous" tone="dark" />
              <h2 className="font-display font-extrabold text-5xl tracking-tight leading-[1] mt-8">
                Parlons de votre <br />
                <span className="text-[#FF5A1F]">prochain chantier.</span>
              </h2>

              <p className="mt-6 max-w-md text-slate-200/90 leading-relaxed text-[15px]">
                Devis gratuit sous 48h · Visite technique offerte ·
                Intervention rapide en cas d'urgence.
              </p>

              <div className="mt-12 grid grid-cols-2 gap-0 border-l border-t border-white/20">
                <ContactCard icon={Phone} label="Téléphone" value={["06 58 11 61 61", "06 89 76 45 77"]} />
                <ContactCard icon={Mail} label="Email" value={["etancia.pro1@gmail.com"]} />
                <ContactCard icon={MapPin} label="Adresse" value={["Ris-Orangis 91130", "Île-de-France & alentours"]} />
                <ContactCard icon={Clock} label="Horaires" value={["Lun – Ven", "8h30-12h30 · 13h30-17h15"]} />
              </div>

              <div className="mt-auto pt-12 flex items-end justify-between gap-6">
                <div>
                  <div className="bg-white inline-flex p-3">
                    <img
                      src="/assets/etancia-logo.png"
                      alt="ETANCIA"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-300/80">
                    SIRET 103 617 387 · Ris-Orangis 91130
                  </p>
                </div>
                <div className="text-right">
                  <div className="label-kicker text-[#FF9A6F]">Site web</div>
                  <div className="font-display font-extrabold text-2xl mt-1">
                    etancia.fr
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <style>{`
        .plaquette { width: 210mm; }
        .page {
          width: 210mm;
          height: 297mm;
          page-break-after: always;
          break-after: page;
          position: relative;
          overflow: hidden;
        }
        .diag-lines {
          background-image: repeating-linear-gradient(
            135deg,
            rgba(255,255,255,0.12) 0,
            rgba(255,255,255,0.12) 1px,
            transparent 1px,
            transparent 14px
          );
        }
        .label-kicker {
          font-family: "Outfit", sans-serif;
          font-weight: 700;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-size: 0.72rem;
        }

        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .plaquette { box-shadow: none !important; width: 210mm; }
          .page { page-break-after: always; height: 297mm; }
          /* Hide global Emergent badge during print */
          #emergent-badge { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function PageHeader({ index, label, tone = "light" }) {
  const lineCol = tone === "dark" ? "bg-[#FF5A1F]" : "bg-[#FF5A1F]";
  const labelCol = tone === "dark" ? "text-[#FF9A6F]" : "text-[#FF5A1F]";
  const indexCol = tone === "dark" ? "text-white/40" : "text-slate-300";
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className={`inline-block w-10 h-px ${lineCol}`} />
        <span className={`label-kicker ${labelCol}`}>{label}</span>
      </div>
      <div className={`font-display font-extrabold text-5xl ${indexCol} leading-none`}>
        {index}
      </div>
    </div>
  );
}

function PageFooter({ pageNum }) {
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-slate-400">
      <span>ETANCIA · Plaquette commerciale</span>
      <span className="font-display font-bold text-slate-700">{pageNum}</span>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value }) {
  return (
    <div className="border-r border-b border-white/20 p-7">
      <Icon className="w-5 h-5 text-[#FF5A1F]" />
      <div className="label-kicker text-[#FF9A6F] mt-5">{label}</div>
      <div className="mt-2 font-display font-bold text-lg leading-tight">
        {value.map((v, i) => (
          <div key={i}>{v}</div>
        ))}
      </div>
    </div>
  );
}
